import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import { FilterValue, IdType } from 'react-table';

import sessionState from 'utils/session-storage';
import { FilterMetadata, RowData, TableSessionData } from 'views/MTable/Table.model';
import { TimeFormat } from 'utils/enums/time-format';

type Options = {
  persistentKey?: string | null;
};

const loadPersistentCache = <TableFields extends RowData>(persistentKey?: string | null): TableSessionData<TableFields> | null => {
  if (!persistentKey) {
    return null;
  }

  const persistentRaw = localStorage.getItem(persistentKey);

  if (!persistentRaw) {
    return null;
  }

  try {
    return JSON.parse(persistentRaw) as TableSessionData<TableFields>;
  } catch {
    return null;
  }
};

const deserializeTableCache = <TableFields extends RowData>(
  sessionData: TableSessionData<TableFields>,
): TableSessionData<TableFields> => {
  const filtersObj = sessionData.filters.map((filter: { id: IdType<TableFields>; value: FilterValue; metadata?: FilterMetadata }) => {
    let objectValue: FilterValue = filter.value;

    // Moment
    if (_.isString(filter.value)) {
      const dateObj = moment(filter.value, TimeFormat.ServerFormatDateTime);

      if (dateObj.isValid()) {
        objectValue = dateObj;
      }
    }

    // Array of moment
    if (_.isArray(filter.value)) {
      objectValue = filter.value.map((value: FilterValue) => {
        const dateObj = moment(value, TimeFormat.ServerFormatDateTime);

        if (dateObj.isValid()) {
          return dateObj;
        }

        return value;
      });
    }

    return { id: filter.id, value: objectValue, metadata: filter.metadata };
  }, {});

  return { ...sessionData, filters: filtersObj };
};

const useTableCacheEffect = <TableFields extends RowData>(
  key: string | null,
  effect: (data: TableSessionData<TableFields> | null) => void,
  dependencies: React.DependencyList,
  options?: Options,
): void => {
  const [cacheState, setCacheState] = useState<{ data: TableSessionData<TableFields> | null; loading: false } | { loading: true }>({
    loading: true,
  });

  useEffect(() => {
    if (!key) {
      setCacheState(() => {
        return { data: null, loading: false };
      });

      return;
    }

    setCacheState(() => {
      return { loading: true };
    });

    sessionState
      .load<TableSessionData<TableFields>>(key)
      .then((sessionData: TableSessionData<TableFields> | null) => {
        if (sessionData !== null) {
          setCacheState(() => {
            return { data: deserializeTableCache(sessionData), loading: false };
          });

          return;
        }

        if (!options?.persistentKey) {
          setCacheState(() => {
            return { data: null, loading: false };
          });

          return;
        }

        const persistentData = loadPersistentCache<TableFields>(options.persistentKey);

        if (!persistentData) {
          setCacheState(() => {
            return { data: null, loading: false };
          });

          return;
        }

        setCacheState(() => {
          return { data: deserializeTableCache(persistentData), loading: false };
        });
      })
      .catch(() => {
        const persistentData = loadPersistentCache<TableFields>(options?.persistentKey);

        setCacheState(() => {
          return { data: persistentData ? deserializeTableCache(persistentData) : null, loading: false };
        });
      });
  }, [key, options?.persistentKey]);

  useEffect(() => {
    if (cacheState.loading) {
      return;
    }

    effect(cacheState.data);
  }, [cacheState, ...dependencies]);
};

export default useTableCacheEffect;
